import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomerLayout from "./CustomerLayout";
import { API_URL } from "../config";
import {
    Plane,
    Car,
    Compass,
    Ticket,
    Globe,
    Wallet,
    Clock,
    ListChecks,
    RefreshCw,
    Loader2,
    ArrowRight,
    Calendar,
    ChevronRight,
} from "lucide-react";

/* ============================================================
   TYPES — mirrors CustomerDashboardController::overview()
   ============================================================ */
interface ServiceCount {
    name: string;
    icon: string;
    count: number;
    link: string;
}
interface StatusCount {
    status: string;
    count: number;
}
interface UpcomingItem {
    type: string;
    icon: string;
    title: string;
    subtitle: string;
    date: string;
    status: string;
    link: string;
}
interface ActivityItem {
    type: string;
    icon: string;
    title: string;
    subtitle: string;
    status: string;
    created_at: string;
}
interface OverviewData {
    profile: { name: string; email: string; member_since: string };
    summary: {
        total_bookings: number;
        upcoming_count: number;
        active_requests: number;
        total_spent: number;
        currency: string;
    };
    service_counts: ServiceCount[];
    status_breakdown: StatusCount[];
    upcoming: UpcomingItem[];
    recent_activity: ActivityItem[];
}

const ICONS: Record<string, React.ElementType> = {
    plane: Plane,
    car: Car,
    passport: Globe,
    compass: Compass,
    ticket: Ticket,
};

const STATUS_COLOR: Record<string, { bg: string; text: string; dot: string }> = {
    Pending: { bg: "#fef3d6", text: "#b25900", dot: "#b25900" },
    Confirmed: { bg: "#e8f0fe", text: "#1a73e8", dot: "#1a73e8" },
    Completed: { bg: "#e6f4ea", text: "#1e7e34", dot: "#1e7e34" },
    Cancelled: { bg: "#fce8e6", text: "#c5221f", dot: "#c5221f" },
};

function statusPillColor(status: string) {
    const s = status.toLowerCase();
    if (["cancelled", "rejected"].includes(s)) return STATUS_COLOR.Cancelled;
    if (["paid", "completed", "delivered", "approved"].includes(s)) return STATUS_COLOR.Completed;
    if (["confirmed", "embassy_processing", "awaiting_payment"].includes(s)) return STATUS_COLOR.Confirmed;
    return STATUS_COLOR.Pending;
}

const DashboardCustomer: React.FC = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<OverviewData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchOverview = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch(`${API_URL}/customer/dashboard/overview`, {
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token_customer_silverjarz") || ""}`,
                },
            });
            if (!response.ok) throw new Error("Failed to load your dashboard.");
            const payload = await response.json();
            if (!payload.success) throw new Error(payload.message || "Failed to load your dashboard.");
            setData(payload.data);
        } catch (err: any) {
            setError(err.message || "Unable to load dashboard.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOverview();
    }, []);

    const maxStatus = data ? Math.max(...data.status_breakdown.map((s) => s.count), 1) : 1;

    return (
        <CustomerLayout>
            <style>{`
                .cdash-container {
                    max-width: 1400px;
                    margin: 0 auto;
                    padding: 0 24px 40px;
                }

                .page-header-modern {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 32px;
                    flex-wrap: wrap;
                    gap: 16px;
                }

                .page-header-modern h1 {
                    font-size: 28px;
                    font-weight: 700;
                    color: #1a1a2e;
                    margin: 0 0 4px 0;
                }

                .page-header-modern p {
                    font-size: 15px;
                    color: #6b7280;
                    margin: 0;
                }

                .btn-refresh {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 20px;
                    background: #ffffff;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    font-size: 13px;
                    font-weight: 600;
                    color: #374151;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .btn-refresh:hover { background: #f9fafb; border-color: #d1d5db; }

                .cdash-stats-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 16px;
                    margin-bottom: 24px;
                }

                .cdash-stat-card {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 20px 24px;
                    border: 1px solid #f0f0f0;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    transition: all 0.3s ease;
                }
                .cdash-stat-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.06); }

                .cdash-stat-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .cdash-stat-content h4 {
                    font-size: 24px;
                    font-weight: 700;
                    color: #1a1a2e;
                    margin: 0 0 2px 0;
                    line-height: 1.2;
                }
                .cdash-stat-content p {
                    font-size: 13px;
                    color: #6b7280;
                    margin: 0;
                }

                .cdash-section {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 22px 24px;
                    border: 1px solid #f0f0f0;
                }

                .cdash-section-title {
                    font-size: 16px;
                    font-weight: 700;
                    color: #1a1a2e;
                    margin: 0 0 2px 0;
                }
                .cdash-section-subtitle {
                    font-size: 12.5px;
                    color: #6b7280;
                    margin: 0 0 18px 0;
                }

                .cdash-two-col {
                    display: grid;
                    grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
                    gap: 16px;
                    margin-bottom: 16px;
                }

                .cdash-services-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
                    gap: 14px;
                    margin-bottom: 24px;
                }

                .cdash-service-card {
                    background: #ffffff;
                    border-radius: 16px;
                    border: 1px solid #f0f0f0;
                    padding: 18px 20px;
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    cursor: pointer;
                    text-decoration: none;
                    transition: all 0.25s ease;
                }
                .cdash-service-card:hover {
                    border-color: #294b5a;
                    box-shadow: 0 8px 24px rgba(41,75,90,0.10);
                    transform: translateY(-2px);
                }
                .cdash-service-icon {
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    background: #f3f4f6;
                    color: #294b5a;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .cdash-service-info h5 {
                    font-size: 14px;
                    font-weight: 700;
                    color: #1a1a2e;
                    margin: 0 0 2px 0;
                }
                .cdash-service-info span {
                    font-size: 12.5px;
                    color: #6b7280;
                }
                .cdash-service-arrow {
                    margin-left: auto;
                    color: #c7ccd1;
                    flex-shrink: 0;
                }

                .cdash-upcoming-item {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    padding: 12px 8px;
                    border-radius: 10px;
                    cursor: pointer;
                    transition: background 0.2s ease;
                }
                .cdash-upcoming-item:hover { background: #f9fafb; }
                .cdash-upcoming-item + .cdash-upcoming-item { border-top: 1px solid #f3f4f6; }

                .cdash-upcoming-date {
                    width: 52px;
                    text-align: center;
                    flex-shrink: 0;
                }
                .cdash-upcoming-date .day {
                    font-size: 18px;
                    font-weight: 700;
                    color: #1a1a2e;
                    line-height: 1.1;
                }
                .cdash-upcoming-date .month {
                    font-size: 10.5px;
                    font-weight: 600;
                    color: #9ca3af;
                    text-transform: uppercase;
                }

                .cdash-upcoming-icon {
                    width: 38px;
                    height: 38px;
                    border-radius: 10px;
                    background: #f3f4f6;
                    color: #294b5a;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .cdash-upcoming-title {
                    font-size: 13.5px;
                    font-weight: 600;
                    color: #1a1a2e;
                }
                .cdash-upcoming-subtitle {
                    font-size: 12px;
                    color: #9ca3af;
                    margin-top: 1px;
                }

                .cdash-status-pill {
                    padding: 3px 10px;
                    border-radius: 20px;
                    font-size: 11px;
                    font-weight: 600;
                    margin-left: auto;
                    flex-shrink: 0;
                }

                .cdash-status-bar-row {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-bottom: 14px;
                }
                .cdash-status-label { width: 76px; font-size: 12px; color: #6b7280; font-weight: 600; flex-shrink: 0; }
                .cdash-status-track { flex: 1; background: #f3f4f6; border-radius: 6px; height: 9px; overflow: hidden; }
                .cdash-status-fill { height: 100%; border-radius: 6px; transition: width 0.6s ease; }
                .cdash-status-count { width: 26px; text-align: right; font-size: 12.5px; font-weight: 700; color: #1a1a2e; flex-shrink: 0; }

                .cdash-feed-item {
                    display: flex;
                    align-items: flex-start;
                    gap: 12px;
                    padding: 10px 0;
                }
                .cdash-feed-item + .cdash-feed-item { border-top: 1px solid #f3f4f6; }
                .cdash-feed-icon {
                    width: 34px;
                    height: 34px;
                    border-radius: 50%;
                    background: #f3f4f6;
                    color: #294b5a;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .cdash-feed-title { font-size: 13px; font-weight: 600; color: #1a1a2e; }
                .cdash-feed-subtitle { font-size: 11.5px; color: #9ca3af; margin-top: 1px; }
                .cdash-feed-time { font-size: 11px; color: #c7ccd1; margin-left: auto; flex-shrink: 0; white-space: nowrap; }

                .cdash-empty {
                    text-align: center;
                    padding: 30px 10px;
                    color: #9ca3af;
                    font-size: 12.5px;
                }

                .loading-state-modern {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 60px 20px;
                    background: #ffffff;
                    border-radius: 16px;
                    border: 1px solid #f0f0f0;
                    gap: 16px;
                }
                .spinner-modern { animation: spin 0.8s linear infinite; color: #294b5a; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

                .error-box-modern {
                    padding: 14px 18px;
                    background: #fef2f2;
                    color: #dc2626;
                    border-radius: 12px;
                    font-size: 14px;
                    font-weight: 500;
                    border: 1px solid #fecaca;
                    margin-bottom: 20px;
                }

                @media (max-width: 991px) {
                    .cdash-two-col { grid-template-columns: 1fr; }
                }
                @media (max-width: 768px) {
                    .cdash-container { padding: 0 16px 30px; }
                    .cdash-stats-grid { grid-template-columns: repeat(2, 1fr); }
                }
                @media (max-width: 480px) {
                    .cdash-stats-grid { grid-template-columns: 1fr 1fr; gap: 10px; }
                    .cdash-stat-card { padding: 14px 16px; }
                    .cdash-stat-content h4 { font-size: 20px; }
                }
            `}</style>

            <div className="cdash-container">
                {/* Header */}
                <div className="page-header-modern">
                    <div>
                        <h1>{data ? `Welcome back, ${data.profile.name.split(" ")[0] || "there"}` : "Welcome back"}</h1>
                        <p>Here's an overview of everything you've booked with us.</p>
                    </div>
                    <button className="btn-refresh" onClick={fetchOverview}>
                        <RefreshCw size={16} />
                        Refresh
                    </button>
                </div>

                {error && <div className="error-box-modern">{error}</div>}

                {loading ? (
                    <div className="loading-state-modern">
                        <Loader2 className="spinner-modern" size={36} />
                        <span style={{ color: "#6b7280" }}>Loading your dashboard...</span>
                    </div>
                ) : data ? (
                    <>
                        {/* SUMMARY STATS */}
                        <div className="cdash-stats-grid">
                            <div className="cdash-stat-card">
                                <div className="cdash-stat-icon" style={{ background: "#e8f0fe", color: "#1a73e8" }}>
                                    <ListChecks size={24} />
                                </div>
                                <div className="cdash-stat-content">
                                    <h4>{data.summary.total_bookings}</h4>
                                    <p>Total Bookings</p>
                                </div>
                            </div>
                            <div className="cdash-stat-card">
                                <div className="cdash-stat-icon" style={{ background: "#fef3d6", color: "#b25900" }}>
                                    <Clock size={24} />
                                </div>
                                <div className="cdash-stat-content">
                                    <h4>{data.summary.upcoming_count}</h4>
                                    <p>Upcoming Trips</p>
                                </div>
                            </div>
                            <div className="cdash-stat-card">
                                <div className="cdash-stat-icon" style={{ background: "#f0ece4", color: "#294b5a" }}>
                                    <Calendar size={24} />
                                </div>
                                <div className="cdash-stat-content">
                                    <h4>{data.summary.active_requests}</h4>
                                    <p>Active Requests</p>
                                </div>
                            </div>
                            <div className="cdash-stat-card">
                                <div className="cdash-stat-icon" style={{ background: "#e6f4ea", color: "#1e7e34" }}>
                                    <Wallet size={24} />
                                </div>
                                <div className="cdash-stat-content">
                                    <h4>{data.summary.currency} {data.summary.total_spent.toLocaleString()}</h4>
                                    <p>Total Spent</p>
                                </div>
                            </div>
                        </div>

                        {/* SERVICE SHORTCUTS */}
                        <div className="cdash-services-grid">
                            {data.service_counts.map((s) => {
                                const Icon = ICONS[s.icon] ?? Globe;
                                return (
                                    <a key={s.name} className="cdash-service-card" onClick={() => navigate(s.link)}>
                                        <div className="cdash-service-icon"><Icon size={20} /></div>
                                        <div className="cdash-service-info">
                                            <h5>{s.count}</h5>
                                            <span>{s.name}</span>
                                        </div>
                                        <ChevronRight size={16} className="cdash-service-arrow" />
                                    </a>
                                );
                            })}
                        </div>

                        {/* UPCOMING + STATUS */}
                        <div className="cdash-two-col">
                            <div className="cdash-section">
                                <h2 className="cdash-section-title">Upcoming</h2>
                                <p className="cdash-section-subtitle">Your next scheduled trips and pickups</p>
                                {data.upcoming.length === 0 && (
                                    <div className="cdash-empty">Nothing scheduled yet — your next booking will show up here.</div>
                                )}
                                {data.upcoming.map((item, i) => {
                                    const Icon = ICONS[item.icon] ?? Globe;
                                    const pill = statusPillColor(item.status);
                                    const [month, day] = item.date.replace(",", "").split(" ");
                                    return (
                                        <div key={i} className="cdash-upcoming-item" onClick={() => navigate(item.link)}>
                                            <div className="cdash-upcoming-date">
                                                <div className="day">{day}</div>
                                                <div className="month">{month}</div>
                                            </div>
                                            <div className="cdash-upcoming-icon"><Icon size={17} /></div>
                                            <div>
                                                <div className="cdash-upcoming-title">{item.title}</div>
                                                <div className="cdash-upcoming-subtitle">{item.type} · {item.subtitle}</div>
                                            </div>
                                            <span className="cdash-status-pill" style={{ background: pill.bg, color: pill.text }}>
                                                {item.status.replace(/_/g, " ")}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="cdash-section">
                                <h2 className="cdash-section-title">Booking Status</h2>
                                <p className="cdash-section-subtitle">Across all services</p>
                                {data.status_breakdown.map((s) => {
                                    const pct = maxStatus > 0 ? (s.count / maxStatus) * 100 : 0;
                                    const color = STATUS_COLOR[s.status]?.dot ?? "#294b5a";
                                    return (
                                        <div key={s.status} className="cdash-status-bar-row">
                                            <span className="cdash-status-label">{s.status}</span>
                                            <div className="cdash-status-track">
                                                <div className="cdash-status-fill" style={{ width: `${pct}%`, background: color }} />
                                            </div>
                                            <span className="cdash-status-count">{s.count}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* RECENT ACTIVITY */}
                        <div className="cdash-section">
                            <h2 className="cdash-section-title">Recent Activity</h2>
                            <p className="cdash-section-subtitle">Your latest actions across every service</p>
                            {data.recent_activity.length === 0 && (
                                <div className="cdash-empty">No activity yet — once you book something, it'll show up here.</div>
                            )}
                            {data.recent_activity.map((item, i) => {
                                const Icon = ICONS[item.icon] ?? Globe;
                                return (
                                    <div key={i} className="cdash-feed-item">
                                        <div className="cdash-feed-icon"><Icon size={16} /></div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div className="cdash-feed-title">{item.title}</div>
                                            <div className="cdash-feed-subtitle">{item.type} · {item.subtitle}</div>
                                        </div>
                                        <span className="cdash-feed-time">{item.created_at}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                ) : null}
            </div>
        </CustomerLayout>
    );
};

export default DashboardCustomer;